import React, { useState } from 'react';
import { func, shape, string } from 'prop-types';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';
import { propTypes } from '../../util/types';
import { pathByRouteName } from '../../util/routes';

import { TERMS_REACCEPTANCE_ENABLED, LATEST_TERMS_VERSION } from '../../config/configTerms';
import { Modal, Button } from '../../components';

import css from './ModalTermsReacceptance.module.css';

// Path (opened in a new tab) to the full Terms of Service page.
const TERMS_OF_SERVICE_PATH = '/terms-of-service';

// Pages where we never show the re-acceptance popup.
//
// This is deliberately its own list instead of reusing
// MISSING_INFORMATION_MODAL_WHITELIST: the two modals have different rules.
// Most importantly, LandingPage is NOT here, because a user who has not
// agreed to the updated terms should be stopped on the landing page too.
//
// The pages below are all places where the popup would block the user from
// doing something they must be able to do (log in, verify email, reset a
// password, read the terms themselves).
export const TERMS_REACCEPTANCE_MODAL_WHITELIST = [
  'LoginPage',
  'ConfirmPage',
  'ContactDetailsPage',
  'EmailVerificationPage',
  'PasswordResetPage',
  'StripePayoutPage',
  'ProfileSettingsPage',
  'TermsOfServicePage',
];

/**
 * A blocking modal that asks a logged-in user to re-agree to the updated
 * Terms of Service. It cannot be closed: the only way forward is the
 * "Agree & Continue" button, which saves the accepted version to the user's
 * protected data.
 *
 * It is hidden on the pages listed in TERMS_REACCEPTANCE_MODAL_WHITELIST
 * (login, signup, email verification, etc.).
 */
const ModalTermsReacceptance = props => {
  const {
    rootClassName,
    className,
    containerClassName,
    currentUser,
    location,
    onManageDisableScrolling,
    onAcceptUpdatedTerms,
  } = props;

  const routeConfiguration = useRouteConfiguration();
  const [inProgress, setInProgress] = useState(false);
  const [error, setError] = useState(null);

  const user = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!user.id;

  // Which version of the terms has this user already accepted?
  const acceptedTermsVersion = user?.attributes?.profile?.protectedData?.acceptedTermsVersion;
  const hasAcceptedLatestTerms = acceptedTermsVersion === LATEST_TERMS_VERSION;

  // Is the current page one where we never want to interrupt the user?
  const whitelistedPaths = TERMS_REACCEPTANCE_MODAL_WHITELIST.map(page =>
    pathByRouteName(page, routeConfiguration)
  );
  const isPageWhitelisted =
    whitelistedPaths.includes(location.pathname) || location.pathname.includes('/signup/');

  const shouldShow =
    TERMS_REACCEPTANCE_ENABLED && currentUserLoaded && !hasAcceptedLatestTerms && !isPageWhitelisted;

  if (!shouldShow) {
    return null;
  }

  const handleAgree = () => {
    setInProgress(true);
    setError(null);
    onAcceptUpdatedTerms(LATEST_TERMS_VERSION)
      .then(() => {
        // On success the current user is refreshed in the store, which makes
        // `hasAcceptedLatestTerms` true and unmounts this modal.
        setInProgress(false);
      })
      .catch(() => {
        setInProgress(false);
        setError(true);
      });
  };

  const classes = classNames(rootClassName || css.root, className);

  const termsLink = (
    <a
      className={css.termsLink}
      href={TERMS_OF_SERVICE_PATH}
      target="_blank"
      rel="noopener noreferrer"
    >
      <FormattedMessage id="ModalTermsReacceptance.termsLinkText" />
    </a>
  );

  return (
    <Modal
      id="TermsReacceptance"
      containerClassName={containerClassName}
      isOpen={true}
      // The user must agree; there is no way to dismiss the modal.
      onClose={() => {}}
      hideCloseBtn={true}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
    >
      <div className={classes}>
        <p className={css.modalTitle}>
          <FormattedMessage id="ModalTermsReacceptance.title" />
        </p>
        <p className={css.modalMessage}>
          <FormattedMessage id="ModalTermsReacceptance.message" values={{ termsLink }} />
        </p>

        {error ? (
          <p className={css.error}>
            <FormattedMessage id="ModalTermsReacceptance.error" />
          </p>
        ) : null}

        <div className={css.bottomWrapper}>
          <Button
            className={css.agreeButton}
            onClick={handleAgree}
            inProgress={inProgress}
            disabled={inProgress}
          >
            <FormattedMessage id="ModalTermsReacceptance.agreeButton" />
          </Button>
        </div>
      </div>
    </Modal>
  );
};

ModalTermsReacceptance.defaultProps = {
  className: null,
  rootClassName: null,
  containerClassName: null,
  currentUser: null,
};

ModalTermsReacceptance.propTypes = {
  className: string,
  rootClassName: string,
  containerClassName: string,

  currentUser: propTypes.currentUser,
  location: shape({
    pathname: string.isRequired,
  }).isRequired,
  onManageDisableScrolling: func.isRequired,
  onAcceptUpdatedTerms: func.isRequired,
};

export default ModalTermsReacceptance;
